import { useState } from "react"
import {
    Box, Button, ButtonGroup, Divider, Flex, HStack, Select, Spinner, Tab, TabList,
    TabPanel, TabPanels, Tabs, Td, Text, useColorModeValue
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { Container, Icon, IconButton, Row, Table, Tag } from "components"
import { API, Enums } from "config"
import { useAllDistributors } from "hooks"
import { FaFileCsv } from "react-icons/fa"

// ─── Hook: movimentações de estoque ──────────────────────────────────────────

const useMovementsReport = (distributorId, filters = {}) => useQuery({
    queryKey: ['report-movements', distributorId, filters],
    queryFn: async () => {
        if (!distributorId) return null
        const params = new URLSearchParams({
            per_page: 100,
            ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v))
        })
        const { data } = await API.get(`/distributors/${distributorId}/stock/movements?${params}`)
        return data
    },
    enabled: !!distributorId,
})

// ─── Hook: perdas de estoque ──────────────────────────────────────────────────

const useLossesReport = (filters = {}) => useQuery({
    queryKey: ['report-losses', filters],
    queryFn: async () => {
        const params = new URLSearchParams({
            per_page: 100,
            ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v))
        })
        const { data } = await API.get(`/stock/losses?${params}`)
        return data
    },
})

// ─── Hook: NF-e ──────────────────────────────────────────────────────────────

const useInvoicesReport = (filters = {}) => useQuery({
    queryKey: ['report-invoices', filters],
    queryFn: async () => {
        const params = new URLSearchParams({
            per_page: 100,
            ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v))
        })
        const { data } = await API.get(`/invoices?${params}`)
        return data
    },
})

// ─── Input de data nativo (sem bug de UTC) ────────────────────────────────────

const DateInput = ({ label, value, onChange }) => {
    const borderColor = useColorModeValue('#CBD5E0', '#4A5568')
    const bg          = useColorModeValue('white',   '#2D3748')
    const color       = useColorModeValue('#1A202C', '#E2E8F0')
    const labelC      = useColorModeValue('gray.500', 'gray.400')

    return (
        <Box>
            <Text fontSize="2xs" fontWeight="bold" textTransform="uppercase"
                letterSpacing="wider" color={labelC} mb={1}>{label}
            </Text>
            <input
                type="date"
                value={value}
                onChange={e => onChange(e.target.value)}
                style={{
                    height: '40px', padding: '0 12px', fontSize: '14px',
                    borderRadius: '6px', border: `1px solid ${borderColor}`,
                    background: bg, color, width: '160px', outline: 'none',
                }}
            />
        </Box>
    )
}

// ─── Label de filtro ──────────────────────────────────────────────────────────

const FilterLabel = ({ children }) => {
    const color = useColorModeValue('gray.500', 'gray.400')
    return (
        <Text fontSize="2xs" fontWeight="bold" textTransform="uppercase" letterSpacing="wider" color={color} mb={1}>
            {children}
        </Text>
    )
}

// ─── Painel de filtros genérico ───────────────────────────────────────────────

const FilterPanel = ({ hasFilters, onClear, children }) => {
    const bg     = useColorModeValue('gray.50',  'gray.700')
    const border = useColorModeValue('gray.100', 'gray.600')
    const lc     = useColorModeValue('gray.500', 'gray.400')

    return (
        <Box bg={bg} border="1px solid" borderColor={border} borderRadius="xl" p={4} mb={4}>
            <Flex justify="space-between" align="center" mb={3}>
                <HStack spacing={2}>
                    <Icon name="search" boxSize={3.5} color={lc} />
                    <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" letterSpacing="widest" color={lc}>
                        Filtros
                    </Text>
                    {hasFilters && <Box w="6px" h="6px" borderRadius="full" bg="teal.400" />}
                </HStack>
                {hasFilters && (
                    <Button size="xs" variant="ghost" colorScheme="red" onClick={onClear}
                        leftIcon={<Icon name="close" boxSize={3} />}>
                        Limpar
                    </Button>
                )}
            </Flex>
            <Flex gap={3} flexWrap="wrap" align="flex-end">{children}</Flex>
        </Box>
    )
}

// ─── Export CSV helper ────────────────────────────────────────────────────────

const downloadCsv = (rows, filename) => {
    const blob = new Blob(["﻿" + rows.join("\n")], { type: "text/csv;charset=utf-8;" })
    const link = document.createElement("a")
    link.href = URL.createObjectURL(blob)
    link.download = filename
    link.click()
}

// ─── Relatório: Movimentações ─────────────────────────────────────────────────

function MovementsReport({ warehouses }) {
    const [distributorId, setDistributorId] = useState('')
    const [type, setType] = useState('')
    const [from, setFrom] = useState('')
    const [to, setTo]     = useState('')

    const filters = {
        ...(type && { type }),
        ...(from && { from }),
        ...(to   && { to }),
    }

    const { data, isFetching, refetch } = useMovementsReport(distributorId, filters)
    const rows = data?.data ?? []

    const hasFilters = !!(type || from || to)
    const clearFilters = () => { setType(''); setFrom(''); setTo('') }

    const typeOptions = [
        { label: 'Todos os tipos',          value: '' },
        { label: 'Entrada por NF-e',        value: Enums.StockMovementType.IN_PURCHASE       },
        { label: 'Liberação de Bloqueio',   value: Enums.StockMovementType.RELEASE_BLOCKED   },
        { label: 'Saída - Transferência',   value: Enums.StockMovementType.TRANSFER_OUT      },
        { label: 'Entrada - Transferência', value: Enums.StockMovementType.TRANSFER_IN       },
        { label: 'Perda',                   value: Enums.StockMovementType.LOSS              },
        { label: 'Ajuste Manual',           value: Enums.StockMovementType.MANUAL_ADJUSTMENT },
    ]

    const handleExport = () => {
        if (!rows.length) return
        const header = "Tipo;Produto;Quantidade;Origem;Destino;NF-e;Funcionário;Data"
        const lines = rows.map(r => [
            Enums.StockMovementType.data(r.type)?.label ?? r.type,
            r.product?.name ?? '',
            r.quantity,
            r.origin_distributor?.name ?? '',
            r.destination_distributor?.name ?? '',
            r.invoice ? `#${r.invoice.number}` : '',
            r.employee?.name ?? '',
            new Date(r.created_at).toLocaleDateString('pt-BR'),
        ].join(";"))
        downloadCsv([header, ...lines], `movimentacoes_${distributorId}.csv`)
    }

    return (
        <Box>
            <FilterPanel hasFilters={hasFilters || !!distributorId} onClear={() => { clearFilters(); setDistributorId('') }}>
                <Box>
                    <FilterLabel>Núcleo *</FilterLabel>
                    <Select size="md" w="240px" value={distributorId} onChange={e => setDistributorId(e.target.value)}>
                        <option value="">Selecione um núcleo</option>
                        {warehouses.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </Select>
                </Box>
                <Box>
                    <FilterLabel>Tipo</FilterLabel>
                    <Select size="md" w="210px" value={type} onChange={e => setType(e.target.value)} isDisabled={!distributorId}>
                        {typeOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </Select>
                </Box>
                <DateInput label="De" value={from} onChange={setFrom} />
                <DateInput label="Até" value={to} onChange={setTo} />
                <Divider orientation="vertical" h="40px" display={{ base: 'none', md: 'block' }} />
                <Flex gap={2} align="center" pt={4}>
                    <IconButton size="sm" icon="refresh" isLoading={isFetching} onClick={refetch} isDisabled={!distributorId} />
                    <Button size="sm" leftIcon={<FaFileCsv />} colorScheme="green" variant="outline"
                        onClick={handleExport} isDisabled={!rows.length}>
                        Exportar CSV
                    </Button>
                </Flex>
            </FilterPanel>

            {!distributorId ? (
                <Flex justify="center" align="center" direction="column" gap={2} py={12} color="gray.400">
                    <Icon name="movement" boxSize={8} />
                    <Text fontSize="sm">Selecione um núcleo para gerar o relatório.</Text>
                </Flex>
            ) : isFetching ? (
                <Flex justify="center" py={12}><Spinner /></Flex>
            ) : (
                <Table columns={[
                    { children: 'Tipo' },
                    { children: 'Produto' },
                    { children: 'Qtd.', isNumeric: true },
                    { children: 'Origem / Destino' },
                    { children: 'NF-e / Ref.' },
                    { children: 'Funcionário' },
                    { children: 'Data', isNumeric: true },
                ]}>
                    {rows.map(item => {
                        const t = Enums.StockMovementType.data(item?.type)
                        const isEntry = item?.quantity > 0
                        return (
                            <Row key={item.id}>
                                <Td><Tag colorScheme={t.colorScheme} size="sm">{t.label}</Tag></Td>
                                <Td fontSize="sm">{item?.product?.name}</Td>
                                <Td isNumeric fontWeight="bold" color={isEntry ? 'green.500' : 'red.500'}>
                                    {isEntry ? '+' : ''}{item?.quantity}
                                </Td>
                                <Td>
                                    <Flex direction="column" gap={0.5}>
                                        {item?.origin_distributor?.name && (
                                            <Text fontSize="xs" color="gray.500">De: {item.origin_distributor.name}</Text>
                                        )}
                                        {item?.destination_distributor?.name && (
                                            <Text fontSize="xs" color="gray.500">Para: {item.destination_distributor.name}</Text>
                                        )}
                                        {!item?.origin_distributor && !item?.destination_distributor && (
                                            <Text fontSize="xs" color="gray.400">—</Text>
                                        )}
                                    </Flex>
                                </Td>
                                <Td>
                                    {item?.invoice
                                        ? <Text fontSize="xs" fontFamily="mono" color="teal.500">NF #{item.invoice.number}</Text>
                                        : <Text fontSize="xs" color="gray.400" noOfLines={1} maxW="120px">{item?.notes || '—'}</Text>}
                                </Td>
                                <Td fontSize="xs">{item?.employee?.name ?? '—'}</Td>
                                <Td isNumeric fontSize="xs">
                                    {new Date(item?.created_at).toLocaleDateString('pt-BR')}
                                </Td>
                            </Row>
                        )
                    })}
                </Table>
            )}
        </Box>
    )
}

// ─── Relatório: Perdas ────────────────────────────────────────────────────────

function LossesReport({ warehouses }) {
    const [distributorId, setDistributorId] = useState('')
    const [from, setFrom] = useState('')
    const [to, setTo]     = useState('')

    const filters = {
        ...(distributorId && { distributor_id: distributorId }),
        ...(from          && { from }),
        ...(to            && { to }),
    }

    const { data, isFetching, refetch } = useLossesReport(filters)
    const rows = data?.data ?? []

    const hasFilters = !!(distributorId || from || to)
    const clearFilters = () => { setDistributorId(''); setFrom(''); setTo('') }

    const handleExport = () => {
        if (!rows.length) return
        const header = "Produto;Lote;Núcleo;Quantidade;Motivo;Registrado por;Aprovação;Data"
        const lines = rows.map(r => [
            r.product?.name ?? '',
            r.lot_number ?? '',
            r.distributor?.name ?? '',
            r.quantity,
            `"${(r.reason ?? '').replace(/"/g, '""')}"`,
            r.employee?.name ?? '',
            r.approved_by ? 'Aprovado' : 'Pendente',
            new Date(r.created_at).toLocaleDateString('pt-BR'),
        ].join(";"))
        downloadCsv([header, ...lines], 'relatorio_perdas.csv')
    }

    return (
        <Box>
            <FilterPanel hasFilters={hasFilters} onClear={clearFilters}>
                <Box>
                    <FilterLabel>Núcleo</FilterLabel>
                    <Select size="md" w="240px" value={distributorId} onChange={e => setDistributorId(e.target.value)}>
                        <option value="">Todos os núcleos</option>
                        {warehouses.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </Select>
                </Box>
                <DateInput label="De" value={from} onChange={setFrom} />
                <DateInput label="Até" value={to} onChange={setTo} />
                <Divider orientation="vertical" h="40px" display={{ base: 'none', md: 'block' }} />
                <Flex gap={2} align="center" pt={4}>
                    <IconButton size="sm" icon="refresh" isLoading={isFetching} onClick={refetch} />
                    <Button size="sm" leftIcon={<FaFileCsv />} colorScheme="green" variant="outline"
                        onClick={handleExport} isDisabled={!rows.length}>
                        Exportar CSV
                    </Button>
                </Flex>
            </FilterPanel>

            {isFetching ? (
                <Flex justify="center" py={12}><Spinner /></Flex>
            ) : (
                <Table columns={[
                    { children: 'Produto' },
                    { children: 'Lote' },
                    { children: 'Núcleo' },
                    { children: 'Qtd.', isNumeric: true },
                    { children: 'Motivo' },
                    { children: 'Registrado por' },
                    { children: 'Aprovação' },
                    { children: 'Data', isNumeric: true },
                ]}>
                    {rows.map(item => (
                        <Row key={item.id}>
                            <Td fontSize="sm" fontWeight="medium">{item?.product?.name}</Td>
                            <Td>
                                {item?.lot_number
                                    ? <Tag colorScheme="purple" size="sm">{item.lot_number}</Tag>
                                    : <Text color="gray.400" fontSize="sm">—</Text>}
                            </Td>
                            <Td fontSize="sm">{item?.distributor?.name}</Td>
                            <Td isNumeric fontWeight="bold" color="red.500">−{item?.quantity}</Td>
                            <Td maxW="200px"><Text noOfLines={2} fontSize="sm">{item?.reason}</Text></Td>
                            <Td fontSize="sm">{item?.employee?.name}</Td>
                            <Td>
                                {item?.approved_by
                                    ? <Tag colorScheme="green" size="sm">Aprovado</Tag>
                                    : <Tag colorScheme="yellow" size="sm">Pendente</Tag>}
                            </Td>
                            <Td isNumeric fontSize="xs">
                                {new Date(item?.created_at).toLocaleDateString('pt-BR')}
                            </Td>
                        </Row>
                    ))}
                </Table>
            )}
        </Box>
    )
}

// ─── Relatório: NF-e ──────────────────────────────────────────────────────────

function InvoicesReport({ warehouses }) {
    const [distributorId, setDistributorId] = useState('')
    const [statusFilter, setStatusFilter]   = useState('')
    const [search, setSearch]               = useState('')
    const [from, setFrom] = useState('')
    const [to, setTo]     = useState('')

    const filters = {
        ...(distributorId && { distributor_id: distributorId }),
        ...(statusFilter  && { status: statusFilter }),
        ...(search        && { search }),
        ...(from          && { from }),
        ...(to            && { to }),
    }

    const { data, isFetching, refetch } = useInvoicesReport(filters)
    const rows = data?.data ?? []

    const hasFilters = !!(distributorId || statusFilter || search || from || to)
    const clearFilters = () => { setDistributorId(''); setStatusFilter(''); setSearch(''); setFrom(''); setTo('') }

    const handleExport = () => {
        if (!rows.length) return
        const header = "Número;Fornecedor;Núcleo;Status;Registrado por;Data Emissão;Data Registro"
        const lines = rows.map(r => [
            r.number ?? '',
            r.supplier_name ?? '',
            r.distributor?.name ?? '',
            Enums.InvoiceStatus.data(r.status)?.label ?? r.status,
            r.employee?.name ?? '',
            r.issued_at ? new Date(r.issued_at).toLocaleDateString('pt-BR') : '',
            new Date(r.created_at).toLocaleDateString('pt-BR'),
        ].join(";"))
        downloadCsv([header, ...lines], 'relatorio_notas_fiscais.csv')
    }

    const statusOptions = [
        { label: 'Todos os status',       value: '' },
        { label: 'Aguardando Carga',      value: Enums.InvoiceStatus.ARRIVING           },
        { label: 'Parcialmente Recebida', value: Enums.InvoiceStatus.PARTIALLY_RECEIVED },
        { label: 'Totalmente Recebida',   value: Enums.InvoiceStatus.FULLY_RECEIVED     },
        { label: 'Rejeitada',             value: Enums.InvoiceStatus.REJECTED           },
        { label: 'Cancelada',             value: Enums.InvoiceStatus.CANCELED           },
    ]

    return (
        <Box>
            <FilterPanel hasFilters={hasFilters} onClear={clearFilters}>
                <Box>
                    <FilterLabel>Núcleo</FilterLabel>
                    <Select size="md" w="220px" value={distributorId} onChange={e => setDistributorId(e.target.value)}>
                        <option value="">Todos os núcleos</option>
                        {warehouses.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </Select>
                </Box>
                <Box>
                    <FilterLabel>Status</FilterLabel>
                    <Select size="md" w="210px" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                        {statusOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </Select>
                </Box>
                <Box>
                    <FilterLabel>Buscar (NF / Fornecedor)</FilterLabel>
                    <input
                        style={{
                            fontSize: '14px', padding: '0 12px', borderRadius: '6px',
                            border: '1px solid var(--chakra-colors-gray-200)',
                            background: 'transparent', height: '40px', width: '210px'
                        }}
                        placeholder="Número ou fornecedor..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </Box>
                <DateInput label="De" value={from} onChange={setFrom} />
                <DateInput label="Até" value={to} onChange={setTo} />
                <Divider orientation="vertical" h="40px" display={{ base: 'none', md: 'block' }} />
                <Flex gap={2} align="center" pt={4}>
                    <IconButton size="sm" icon="refresh" isLoading={isFetching} onClick={refetch} />
                    <Button size="sm" leftIcon={<FaFileCsv />} colorScheme="green" variant="outline"
                        onClick={handleExport} isDisabled={!rows.length}>
                        Exportar CSV
                    </Button>
                </Flex>
            </FilterPanel>

            {isFetching ? (
                <Flex justify="center" py={12}><Spinner /></Flex>
            ) : (
                <Table columns={[
                    { children: 'Número' },
                    { children: 'Fornecedor' },
                    { children: 'Núcleo' },
                    { children: 'Status' },
                    { children: 'Registrado por' },
                    { children: 'Emissão' },
                    { children: 'Registro', isNumeric: true },
                ]}>
                    {rows.map(item => {
                        const s = Enums.InvoiceStatus.data(item?.status)
                        return (
                            <Row key={item.id}>
                                <Td fontFamily="mono" fontSize="sm">{item?.number}</Td>
                                <Td fontSize="sm">{item?.supplier_name}</Td>
                                <Td fontSize="sm">{item?.distributor?.name}</Td>
                                <Td><Tag colorScheme={s.colorScheme} size="sm">{s.label}</Tag></Td>
                                <Td fontSize="sm">{item?.employee?.name}</Td>
                                <Td fontSize="xs">
                                    {item?.issued_at ? new Date(item.issued_at).toLocaleDateString('pt-BR') : '—'}
                                </Td>
                                <Td isNumeric fontSize="xs">
                                    {new Date(item?.created_at).toLocaleDateString('pt-BR')}
                                </Td>
                            </Row>
                        )
                    })}
                </Table>
            )}
        </Box>
    )
}

// ─── Componente raiz ──────────────────────────────────────────────────────────

export default function StockReports() {
    const distributors = useAllDistributors()
    const warehouses   = distributors.data?.filter(d => d.is_warehouse) ?? []

    return (
        <Container maxW="100%" mt={6}>
            <Tabs isLazy colorScheme="teal">
                <TabList>
                    <Tab fontSize="sm" gap={1.5}>
                        <Icon name="movement" boxSize={4} />
                        Movimentações
                    </Tab>
                    <Tab fontSize="sm" gap={1.5}>
                        <Icon name="loss" boxSize={4} />
                        Perdas
                    </Tab>
                    <Tab fontSize="sm" gap={1.5}>
                        <Icon name="invoice" boxSize={4} />
                        Notas Fiscais
                    </Tab>
                </TabList>
                <TabPanels>
                    <TabPanel px={0} pt={4}>
                        <MovementsReport warehouses={warehouses} />
                    </TabPanel>
                    <TabPanel px={0} pt={4}>
                        <LossesReport warehouses={warehouses} />
                    </TabPanel>
                    <TabPanel px={0} pt={4}>
                        <InvoicesReport warehouses={warehouses} />
                    </TabPanel>
                </TabPanels>
            </Tabs>
        </Container>
    )
}
